import { useMemo } from 'react';

import type { Channel } from '../richTextField';

type UseWhatsAppExclusiveModeProps = {
  /** The current channel */
  channel: Channel;
  /** Whether the WhatsApp outbound attachments feature flag is enabled */
  isWhatsAppAttachmentsEnabled: boolean;
  /** The current plain text content in the editor */
  editorPlainText: string;
  /** The current attachments */
  attachments?: File[];
};

type UseWhatsAppExclusiveModeResult = {
  /** Whether the current channel is WhatsApp with attachments enabled */
  isWhatsAppExclusiveMode: boolean;
  /** Whether the editor has text content */
  hasTextContent: boolean;
  /** Whether there are attachments */
  hasAttachments: boolean;
  /** Whether attachments should be disabled (text is present) */
  isAttachmentsDisabledByText: boolean;
  /** Whether text input should be disabled (attachment is present) */
  isTextDisabledByAttachment: boolean;
  /** Tooltip message for disabled attachment button */
  attachmentsDisabledTooltip: string | undefined;
  /** Placeholder message when text input is disabled */
  textDisabledPlaceholder: string | undefined;
};

/**
 * Hook to manage WhatsApp's exclusive text/attachment mode.
 *
 * WhatsApp only supports sending text OR attachments per message, not both.
 * This hook provides the state and messages needed to enforce this
 * restriction.
 */
export const useWhatsAppExclusiveMode = ({
  channel,
  isWhatsAppAttachmentsEnabled,
  editorPlainText,
  attachments,
}: UseWhatsAppExclusiveModeProps): UseWhatsAppExclusiveModeResult => {
  const isWhatsAppExclusiveMode = useMemo(() => {
    return channel === 'whatsapp' && isWhatsAppAttachmentsEnabled;
  }, [channel, isWhatsAppAttachmentsEnabled]);

  const hasTextContent = useMemo(() => {
    return editorPlainText.trim().length > 0;
  }, [editorPlainText]);

  const hasAttachments = useMemo(() => {
    return Array.isArray(attachments) && attachments.length > 0;
  }, [attachments]);

  const isAttachmentsDisabledByText = useMemo(() => {
    // Only disable adding attachments if there's text but NO attachments yet.
    // If both exist, allow editing so user can fix the situation.
    return isWhatsAppExclusiveMode && hasTextContent && !hasAttachments;
  }, [isWhatsAppExclusiveMode, hasTextContent, hasAttachments]);

  const isTextDisabledByAttachment = useMemo(() => {
    // Only disable text input if there are attachments but NO text yet.
    // If both exist, allow editing so user can fix the situation.
    return isWhatsAppExclusiveMode && hasAttachments && !hasTextContent;
  }, [isWhatsAppExclusiveMode, hasAttachments, hasTextContent]);

  const attachmentsDisabledTooltip = useMemo(() => {
    if (!isAttachmentsDisabledByText) {
      return undefined;
    }
    return 'Remove text to attach a file.';
  }, [isAttachmentsDisabledByText]);

  const textDisabledPlaceholder = useMemo(() => {
    if (!isTextDisabledByAttachment) {
      return undefined;
    }
    return 'Remove attachment to type a message';
  }, [isTextDisabledByAttachment]);

  return {
    isWhatsAppExclusiveMode,
    hasTextContent,
    hasAttachments,
    isAttachmentsDisabledByText,
    isTextDisabledByAttachment,
    attachmentsDisabledTooltip,
    textDisabledPlaceholder,
  };
};
